import { isExperimentApprovalOptInEnabled } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { Experiment } from '@gonfalon/experiments/types';
import { Alert, Button, Heading, IconButton, Text } from '@launchpad-ui/components';

import { TurnOnFlagAlert } from './TurnOnFlagAlert';

import styles from './StartExperimentModal.module.css';

type StartExperimentModalProps = {
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
  isMAB: boolean;
  isHoldout?: boolean;
  isFlagOff?: boolean;
  experiment?: Experiment;
  flagKey?: string;
  hasStratifiedSampling?: boolean;
};

export function StartExperimentModal({
  onCancel,
  onConfirm,
  isPending,
  isMAB,
  isHoldout = false,
  isFlagOff = false,
  experiment,
  flagKey,
  hasStratifiedSampling = false,
}: StartExperimentModalProps) {
  const typeString = isMAB ? 'multi-armed bandit' : isHoldout ? 'holdout' : 'experiment';

  return (
    <>
      <div slot="header">
        {!isHoldout && (
          <Heading slot="title" aria-label={`Start ${typeString}`}>
            Start {typeString}
          </Heading>
        )}
        {isHoldout && (
          <Heading aria-label="Declare holdout ready">
            Declare ready to include experiments and multi-armed bandits?
          </Heading>
        )}
        <IconButton aria-label="close modal" icon="cancel" variant="minimal" onPress={onCancel} />
      </div>
      <div slot="body">
        {!isHoldout && (
          <Text slot="subtitle">
            This {typeString} will begin serving the variations you specified when you set the {typeString} audience,
            and will start recording results.
          </Text>
        )}
        {isHoldout && (
          <Text slot="subtitle">
            You will no longer be able to make changes to your holdout's configuration except for the name, description,
            and statistical approach.
          </Text>
        )}
        {isExperimentApprovalOptInEnabled() && flagKey && (
          <TurnOnFlagAlert typeString={typeString} experiment={experiment} flagKey={flagKey} />
        )}
        {hasStratifiedSampling && isPending && (
          <Alert variant="inline" status="info" className={styles.stratifiedSamplingAlert}>
            We're setting up stratified sampling to make sure your experiment groups are well balanced — this can take a
            bit longer than usual.
          </Alert>
        )}
      </div>
      <div slot="footer">
        <Button variant="default" onPress={onCancel} isDisabled={isPending}>
          Cancel
        </Button>
        <Button variant="primary" onPress={onConfirm} isPending={isPending} isDisabled={isPending || isFlagOff}>
          {!isHoldout && `Start ${typeString}`}
          {isHoldout && `Declare ready`}
        </Button>
      </div>
    </>
  );
}
